import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { catchError, delay, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AppService } from '../_shared/services/app.service';
import { PermissionService } from '../_shared/services/permission.service';
import { ProductsCategoryService } from '../_shared/services/products-category.service';
import { ProductsService } from '../_shared/services/products.service';
import { QuoteDiscountsService } from '../_shared/services/quote-discounts.service';
import { QuoteService } from '../_shared/services/quote.service';
import { SelectedCustomerService } from '../_shared/services/selected-customer.service';
import { SnackBarService } from '../_shared/services/snack-bar.service';
import { UnitTestsService } from '../_shared/services/unit-tests.service';
import { OverviewDataSource } from './overview-data-source';
import { CopyOfferParams, OverviewDataService, SearchParams, UseTemplateParams } from './overview.data.service';
import { EnvironmentDiffService } from '../_shared/services/environment-diff.service';
import { ProductListService } from '../_shared/components/product-list/product-list.service';

@Component({
    selector: 'app-overview',
    templateUrl: './overview.component.html',
    styleUrls: ['./overview.component.scss'],
    standalone: false
})
export class OverviewComponent implements OnInit {
    private isOffersLoading = true;
    private isTemplatesLoading = true;

    private templateSubscription: Subscription;

    public loaderText: string;
    public overviewTableDataSource: OverviewDataSource<any, SearchParams>;
    public templatesDataSource: OverviewDataSource<any, SearchParams>;
    public unitTestsData = new BehaviorSubject<any[]>([]);

    public selectedTab: 'offers' | 'templates' | 'unit-tests' = 'offers';

    constructor(
        public dataService: OverviewDataService,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
        private productsService: ProductsService,
        private router: Router,
        private permissionService: PermissionService,
        private selectedCustomerService: SelectedCustomerService,
        private quoteDiscountsService: QuoteDiscountsService,
        public appService: AppService,
        private categoryService: ProductsCategoryService,
        private environmentDiffService: EnvironmentDiffService,
        private quoteService: QuoteService,
        private productListService: ProductListService,
        private unitTestsService: UnitTestsService
    ) {}

    public ngOnInit() {
        this.quoteService.reset();
        this.selectedCustomerService.clearSelectedCustomer();
        this.productsService.clearPriceSummaryCache();
        this.productListService.clearEdits();
        this.categoryService.clearCache();
        this.environmentDiffService.clearCache();

        this.overviewTableDataSource = new OverviewDataSource<any, SearchParams>(
            this.appService,
            this.selectedCustomerService,
            (p) => this.dataService.searchOffers(p)
        );
        this.overviewTableDataSource.loading$.subscribe((loading) => {
            this.isOffersLoading = loading;
        });
        this.templatesDataSource = new OverviewDataSource<any, SearchParams>(
            this.appService,
            this.selectedCustomerService,
            (p) => this.dataService.searchTemplates(p)
        );
        this.templatesDataSource.loading$.subscribe((loading) => (this.isTemplatesLoading = loading));
        this.appService.salesOrg$.subscribe(() => {
            this.getOfferList();
            this.getTemplateList();
            this.getUnitTests();
        });
        this.manageActiveTabs();
    }

    mapActiveTab(activeTab) {
        switch (activeTab) {
            case 'offers': {
                this.selectedTab = 'offers';
                this.setLoaderText('OVERVIEW.LOADER.LOADING_OFFERS');
                break;
            }
            case 'templates': {
                this.selectedTab = 'templates';
                this.setLoaderText('OVERVIEW.LOADER.LOADING_TEMPLATES');
                break;
            }
            case 'unit-tests': {
                this.selectedTab = 'unit-tests';
                this.setLoaderText('OVERVIEW.LOADER.LOADING_UNIT_TESTS');
                break;
            }
        }
    }

    manageActiveTabs() {
        return this.mapActiveTab(this.router.url.split('/').pop());
    }

    public getUnitTests() {
        this.unitTestsService
            .getUnitTests()
            .pipe(map((data) => data.items))
            .subscribe(
                (items) => {
                    this.unitTestsData.next(items);
                },
                () => this.onError('OVERVIEW.SNACK_BAR.FETCH_UNIT_TESTS.FAILURE')
            );
    }

    public get isPartnerMode() {
        return !!this.selectedCustomerService.customerForPartnerMode$?.value;
    }

    public get isEmployee() {
        return this.permissionService.isUserAnEmployee;
    }

    public get isAdmin() {
        return this.permissionService.isCurrentUserAdmin();
    }

    public get showTemplatesTab() {
        return this.appService.isAuthorizedToUseTemplates;
    }

    public get isLoading() {
        return this.selectedTab === 'offers' ? this.isOffersLoading : this.isTemplatesLoading;
    }

    public onViewOffer({ id, status, quoteId }) {
        this.quoteDiscountsService.clearQuoteDiscounts();

        if (status && status.toLowerCase() !== 'new') {
            this.router.navigate(['next-steps'], { queryParams: { id, sapDocumentId: quoteId } });
        } else {
            this.router.navigate(['configuration'], {
                queryParams: { id },
            });
        }
    }

    public onDeleteOffer({ id, status, reason }) {
        this.isOffersLoading = true;
        this.deleteOffer({ id, status, reason });
    }

    private setLoaderText(key: string) {
        // use instant first to avoid visible key while translations are being fetched
        this.loaderText = this.translateService.instant(key);
        // this makes sure we use the right language right after selecting a language from dropdown
        this.translateService.get(key).subscribe((res) => (this.loaderText = res));
    }

    public onCopyOffer(params: CopyOfferParams) {
        this.setLoaderText('OVERVIEW.LOADER.COPYING_OFFER');
        this.isOffersLoading = true;
        this.copyOffer(params);
    }

    public onUseTemplate({ id, customerPurchaseNumber }) {
        this.setLoaderText('OVERVIEW.LOADER.CREATING_OFFER');
        this.isTemplatesLoading = true;
        this.useTemplate({ id, customerPurchaseNumber });
    }

    public createNewOffer() {
        const customerPurchaseNumber = this.translateService.instant('COMMON.NEW_OFFER');

        this.setLoaderText('OVERVIEW.LOADER.CREATING_OFFER');
        this.isOffersLoading = true;

        this.dataService.createNewOffer({ customerPurchaseNumber, model: environment.quest.project }).subscribe({
            next: (response) => {
                this.quoteDiscountsService.clearQuoteDiscounts();
                this.onSuccess('OVERVIEW.SNACK_BAR.CREATE_OFFER.SUCCESS');
                this.router.navigate(['configuration'], { queryParams: { id: response.id } });
            },
            error: () => this.onError('OVERVIEW.SNACK_BAR.CREATE_OFFER.FAILURE'),
        });
    }

    public onSearch($event) {
        if (this.selectedTab === 'offers') {
            this.isOffersLoading = true;
            this.searchForOffers($event);
        } else {
            this.isTemplatesLoading = true;
            this.searchForTemplates($event);
        }
    }

    private deleteOffer(params: { id: string; status: string; reason: string }) {
        this.dataService
            .deleteOffer(params)
            .pipe(delay(100))
            .subscribe({
                next: () => {
                    this.onSuccess('OVERVIEW.SNACK_BAR.DELETE_OFFER.SUCCESS');
                    this.getOfferList();
                },
                error: () => this.onError('OVERVIEW.SNACK_BAR.DELETE_OFFER.FAILURE'),
            });
    }

    public getOfferList() {
        this.setLoaderText('OVERVIEW.LOADER.LOADING_OFFERS');
        this.overviewTableDataSource.resetPaging();
        this.overviewTableDataSource
            .loadOverviewData()
            .pipe(catchError(() => of(this.onError('OVERVIEW.SNACK_BAR.FETCH_OFFERS.FAILURE'))))
            .subscribe();
    }

    public getTemplateList() {
        this.templatesDataSource.resetPaging();
        if (this.templateSubscription) {
            this.templateSubscription.unsubscribe();
        }
        this.templateSubscription = this.templatesDataSource
            .loadOverviewData({ sortBy: 'title', sortOrder: 'asc', pageSize: 10 })
            .pipe(catchError(() => of(this.onError('OVERVIEW.SNACK_BAR.FETCH_TEMPLATES.FAILURE'))))
            .subscribe();
    }

    public onPagingError() {
        this.onError('OVERVIEW.SNACK_BAR.PAGING.FAILURE');
    }

    public onSortError() {
        this.onError('OVERVIEW.SNACK_BAR.SORTING.FAILURE');
    }

    public onCustomerSelected() {
        this.getOfferList();
    }

    private searchForOffers(parameters) {
        this.overviewTableDataSource.resetPaging();
        this.overviewTableDataSource
            .loadOverviewData({ parameters })
            .pipe(catchError(() => of(this.onError('OVERVIEW.SNACK_BAR.SEARCH_OFFER.FAILURE'))))
            .subscribe();
    }

    private searchForTemplates(parameters) {
        this.templatesDataSource.resetPaging();
        this.templatesDataSource
            .loadOverviewData({ parameters, sortBy: 'title', sortOrder: 'asc', pageSize: 10 })
            .pipe(catchError(() => of(this.onError('OVERVIEW.SNACK_BAR.SEARCH_TEMPLATE.FAILURE'))))
            .subscribe();
    }

    private copyOffer(params: CopyOfferParams) {
        this.dataService.copyOffer(params).subscribe({
            next: (response) => {
                this.onSuccess('OVERVIEW.SNACK_BAR.COPY_OFFER.SUCCESS');
                this.navigateToConfiguration(response.id);
            },
            error: () => this.onError('OVERVIEW.SNACK_BAR.COPY_OFFER.FAILURE'),
        });
    }

    private useTemplate(params: UseTemplateParams) {
        this.dataService.useTemplate(params).subscribe({
            next: (response) => {
                this.onSuccess('OVERVIEW.SNACK_BAR.USE_TEMPLATE.SUCCESS');
                this.navigateToConfiguration(response.id);
            },
            error: () => this.onError('OVERVIEW.SNACK_BAR.USE_TEMPLATE.FAILURE'),
        });
    }

    public navigateToConfiguration(id: string) {
        this.quoteDiscountsService.clearQuoteDiscounts();
        this.router.navigate(['configuration'], { queryParams: { id } });
    }

    private onError(translationKey: string) {
        this.isOffersLoading = false;
        this.isTemplatesLoading = false;
        if (this.permissionService.isUserAuthorized$.value) {
            setTimeout(() => {
                this.snackBarService.openSnackBar({
                    message: this.translateService.instant(translationKey),
                    isFailure: true,
                });
            }, 200);
        }
    }

    private onSuccess(translationKey: string) {
        this.snackBarService.openSnackBar({
            message: this.translateService.instant(translationKey),
        });
    }

    public onReloadTemplates() {
        this.getTemplateList();
        this.getOfferList();
    }

    public onReloadUnitTests() {
        this.getUnitTests();
        this.getOfferList();
    }
}
